/**
 * VelocityIndicator.tsx
 * -----------------------------------------------------------------------------
 * Displays sphere velocity (momentum) with directional arrow and score.
 * Shows whether a sphere's status is improving, stable, or declining.
 */

import React from 'react';
import { useTranslation } from 'localization';
import type { VelocityBucketKey } from 'types/sphereMetrics';
import './VelocityIndicator.scss';

export interface VelocityIndicatorProps {
  /** Velocity score from -100 to +100 */
  score: number;
  /** Velocity bucket for styling */
  bucket: VelocityBucketKey;
  /** Show numeric score (default: false) */
  showScore?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Additional CSS class */
  className?: string;
}

/**
 * Get arrow rotation based on velocity direction
 */
function getArrowRotation(bucket: VelocityBucketKey): number {
  switch (bucket) {
    case 'rapidly_improving':
      return -45; // Arrow pointing up-right (strongly positive)
    case 'improving':
      return -30; // Arrow pointing slightly up
    case 'stable':
      return 0; // Arrow pointing right (neutral)
    case 'declining':
      return 30; // Arrow pointing slightly down
    case 'rapidly_declining':
      return 45; // Arrow pointing down-right (strongly negative)
    default:
      return 0;
  }
}

/**
 * VelocityIndicator - Visual indicator for sphere momentum/velocity
 */
const VelocityIndicator: React.FC<VelocityIndicatorProps> = ({
  score,
  bucket,
  showScore = false,
  size = 'medium',
  className = ''
}) => {
  const { t } = useTranslation();
  const rotation = getArrowRotation(bucket);
  const bucketClass = `velocity-indicator--${bucket.replaceAll('_', '-')}`;
  const sizeClass = `velocity-indicator--${size}`;

  // Get localized label for the bucket
  const bucketLabel = t(`velocity.buckets.${bucket}`);

  // Build title string - extract nested ternary for clarity
  const scorePrefix = score > 0 ? '+' : '';
  const scoreDisplay = showScore ? ` (${scorePrefix}${score})` : '';
  const titleText = `${bucketLabel}${scoreDisplay}`;

  return (
    <div
      className={`velocity-indicator ${bucketClass} ${sizeClass} ${className}`}
      title={titleText}
    >
      <svg
        className="velocity-indicator__arrow"
        viewBox="0 0 24 24"
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        <path
          d="M5 12h14M13 5l7 7-7 7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {showScore && (
        <span className="velocity-indicator__score">
          {score > 0 ? '+' : ''}{score}
        </span>
      )}
    </div>
  );
};

export default VelocityIndicator;
